import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import logoUrl from "@/logo.png";

interface SharedHeaderProps {
  onCTAClick?: (mode: 'login' | 'signup') => void;
}

export default function SharedHeader({ onCTAClick }: SharedHeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCTAClick = (mode: 'login' | 'signup') => {
    if (onCTAClick) {
      onCTAClick(mode);
    } else {
      // Navigate to home page with hash to trigger email focus
      navigate(mode === 'login' ? "/#login" : "/#signup");
    }
  };

  const handleLoginClick = () => {
    setIsMobileMenuOpen(false);
    handleCTAClick('login');
  };

  const handleSignupClick = () => {
    setIsMobileMenuOpen(false);
    handleCTAClick('signup');
  };

  const handleLogout = () => {
    setIsMobileMenuOpen(false);
    logout();
  };

  return (
    <header className={cn(
      "fixed top-0 left-0 right-0 z-50",
      theme.colors.backgroundCream,
      "border-b-3 border-gray-900 dark:border-gray-300"
    )}>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 md:px-6 py-3 md:py-4">
        {/* Logo/Icon */}
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <img
            src={logoUrl}
            alt="PodWise GPT"
            className="h-8 md:h-10 w-auto"
          />
          <span className="font-bold text-lg text-gray-900 dark:text-white">PodWise GPT</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
          <Link to="/pricing" className="hover:underline text-gray-900 dark:text-white">
            Pricing
          </Link>
          <Link to="/faq" className="hover:underline text-gray-900 dark:text-white">
            FAQ
          </Link>
        </nav>

        {/* Desktop CTA Buttons */}
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <Button
              onClick={handleLogout}
              variant="outline"
              className={cn(
                theme.buttons.secondary,
                "font-semibold text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
              )}
            >
              Log Out
            </Button>
          ) : (
            <>
              <Button
                onClick={handleLoginClick}
                variant="outline"
                className={cn(
                  theme.buttons.secondary,
                  "font-semibold"
                )}
              >
                Log In
              </Button>
              <Button
                onClick={handleSignupClick}
                className={cn(
                  theme.buttons.primary,
                  "font-semibold"
                )}
              >
                Get Started <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
        </div>

        {/* Mobile: Hamburger menu on the right */}
        <>
          {isMobileMenuOpen && (
            <div 
              className="fixed inset-0 bg-black/20 dark:bg-black/40 z-40 md:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}
          <div className="flex md:hidden items-center">
            <div className="relative" ref={mobileMenuRef}>
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(prev => !prev)}
                className={cn(
                  "flex items-center justify-center h-10 w-10 rounded-full border-2 border-gray-900 dark:border-gray-300",
                  "bg-white dark:bg-gray-800",
                  theme.shadows.neubrutalism2,
                  "transition-transform hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-4 focus:ring-primary-orange/30 dark:focus:ring-primary-orange-dark/30",
                )}
                aria-label="Menu"
              >
                {isMobileMenuOpen ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5 text-gray-900 dark:text-white">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5 text-gray-900 dark:text-white">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
              {isMobileMenuOpen && (
                <div className={cn(
                  "absolute right-0 mt-2 w-56 rounded-xl border-2 border-gray-900 dark:border-gray-300",
                  theme.colors.cardCream,
                  theme.shadows.neubrutalismLg,
                  "overflow-hidden z-50",
                )}>
                  <nav className="flex flex-col">
                    <Link
                      to="/pricing"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="px-4 py-3 text-base font-semibold text-gray-900 dark:text-gray-100 hover:bg-primary-orange/10 dark:hover:bg-primary-orange-dark/20 transition-colors"
                    >
                      Pricing
                    </Link>
                    <Link
                      to="/faq"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="px-4 py-3 text-base font-semibold text-gray-900 dark:text-gray-100 hover:bg-primary-orange/10 dark:hover:bg-primary-orange-dark/20 transition-colors border-t-2 border-gray-900 dark:border-gray-300"
                    >
                      FAQ
                    </Link>
                    {user ? (
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="px-4 py-3 text-left text-base font-semibold text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors border-t-2 border-gray-900 dark:border-gray-300"
                      >
                        Log Out
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={handleLoginClick}
                          className="px-4 py-3 text-left text-base font-semibold text-gray-900 dark:text-gray-100 hover:bg-primary-orange/10 dark:hover:bg-primary-orange-dark/20 transition-colors border-t-2 border-gray-900 dark:border-gray-300"
                        >
                          Log In
                        </button>
                        <div className="px-4 py-3 border-t-2 border-gray-900 dark:border-gray-300">
                          <Button
                            onClick={handleSignupClick}
                            className={cn(
                              theme.buttons.primary,
                              "w-full font-semibold justify-center"
                            )}
                          >
                            Get Started <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </>
                    )}
                  </nav>
                </div>
              )}
            </div>
          </div>
        </>
      </div>
    </header>
  );
}
